/* eslint-disable @typescript-eslint/camelcase */

import { createStore } from 'vuex';
import axios from 'axios';
import qs from 'qs';
import router from './router';
import Cookies from 'js-cookie';
import md5 from 'md5';


// declare custom window with our hashChangeEvent to communicate with outside scripts
interface CustomWindow extends Window {
  hashChangeEvent?: Event;
}
declare const window: CustomWindow;

// Vue.use(Vuex);

export default createStore({

    state: {
        region: 0,
        endpoints: {} as any,
        selected: {} as any,
        disabled: [] as string[],
        sorting: '',
        // geojson: [] as GeoJsonFeature[],
        filters: [] as any,
        focus: '',
        title: '',
        intro: '',
        results: '',
        loading: false,
        errors: [],
        count: 0,
        page: 1,
        pages: 1,
        alerts: [] as string[],
        mode: 'listings',
        appDisabled: false,
        role: '',
        mapCenter: [-123.0770821, 49.3218825] as [number, number],
        mapZoom: 13.8,
    },

    getters: {

        filtersMain: (state) => {
            return state.filters.filter((f: any) => (f.priority <= 1));
        },
        filtersMore: (state) => {
            return state.filters.filter((f: any) => (f.priority > 1));
        },
        filtersMoreNames: (state, getters) => {
            // return getters.filtersMore.map((f: any) => f.name );
            const names = [];
            for (const f of getters.filtersMore) {
                if (f.type === 'fromto') {
                    names.push(f.name + '_from');
                    names.push(f.name + '_to');
                } else {
                    names.push(f.name);
                }
            }
            return names;
        },
        filterByName: (state) => (name: string) => {
            const filter = state.filters.find((ff: any) => (ff.name === name)) as any;
            return filter;
        },

        // find out if the more menu should remain open.
        moreOpened: (state, getters) => {
            return state.focus === 'more' || getters.filtersMoreNames.indexOf(state.focus) !== -1;
        },

        // create dict of data to send to the server
        serverData: (state) => {

            const data: any = {};

            for (const f of state.filters) {
                const sel = state.selected[f.name];
                switch (f.type) {
                    case 'minmax':
                    case 'minmax-manual':
                        if (sel.min) { data[f.name + '_min'] = sel.min; }
                        if (sel.max) { data[f.name + '_max'] = sel.max; }
                        break;
                    case 'fromto':
                        if (sel.from) { data[f.name + '_from'] = sel.from; }
                        if (sel.to) { data[f.name + '_to'] = sel.to; }
                        break;
                    case 'multi':
                        if (sel && sel.length) { data[f.name] = sel; }
                        break;
                    default:
                        if (sel) { data[f.name] = sel; }
                }
            }

            if (state.sorting) {
                data.order_by = state.sorting;
                if (state.sorting === 'dst') {
                    if (state.mapCenter[0] && state.mapCenter[1]) {
                        data.lng = state.mapCenter[0];
                        data.lat = state.mapCenter[1];
                    }
                    if (state.mapZoom) {
                        data.zoom = state.mapZoom;
                    }
                }
            }

            // console.log(data);
            return data;
        },

        alertData: (state, getters) => {
            const data = { ...getters.serverData };
            // don't consider sorting when generating a hash or setting alerts
            if (data.order_by) { delete data.order_by; }
            if (data.lat) { delete data.lat; }
            if (data.lng) { delete data.lng; }
            if (data.zoom) { delete data.zoom; }
            return data;
        },

        currentHash: (state, getters) => {
            const data = { ...getters.alertData };
            data.region = state.region;
            data.alert_type = state.mode;
            const json = JSON.stringify(data, Object.keys(data).sort());
            // console.log(json);
            return md5(json);
        },

        selectedQuery: (state, getters) => {
            return qs.stringify(getters.alertData, { indices: false, skipNulls: true });
        },
        alertExists: (state, getters) => {
            return state.alerts.includes(getters.currentHash);
        },
        shortRegion: (state) => {
            switch (state.region) {
                case 1:
                    return 'nv'
                case 2:
                default:
                    return 'wv';
            }
        },

    },

    mutations: {

        INITIALIZE(state, payload) {
            // filters, selected, sorting
            state.endpoints = payload.endpoints;
            state.alerts = payload.alerts;
            state.region = parseInt(payload.region, 10);
            state.filters = payload.filters;
            state.selected = payload.selected;
            state.sorting = payload.sorting;
            state.disabled = payload.disabled;
            state.mode = payload.mode;
            state.appDisabled = payload.appDisabled;
            state.role = payload.role;
            if (payload.mapCenter) {
                state.mapCenter = payload.mapCenter;
            }
            if (payload.mapZoom) {
                state.mapZoom = payload.mapZoom;
            }
        },

        SET_LOADING(state, payload: boolean) {
            state.loading = payload;
        },

        SET_FOCUS(state, payload: string) {
            state.focus = payload;
        },

        SET_PAGE(state, payload: number) {
            state.page = payload;
        },

        APPLY_FILTER(state, payload: any) {
            // const {filter, option} = payload;
            state.selected[payload.filter] = payload.option;
        },

        APPLY_FILTER_MULTI(state, payload: any) {
            const {filter, value} = payload;
            state.selected[filter] = value;
        },

        APPLY_FILTER_MIN(state, payload: any) {
            const {filter, min} = payload;
            state.selected[filter].min = min;
        },

        APPLY_FILTER_MAX(state, payload: any) {
            state.selected[payload.filter].max = payload.max;
        },

        APPLY_FILTER_FROM(state, payload: any) {
            const {filter, from} = payload;
            state.selected[filter].from = from;
        },

        APPLY_FILTER_TO(state, payload: any) {
            state.selected[payload.filter].to = payload.to;
        },

        APPLY_SORT(state, option: string) {
            state.sorting = option;
        },

        SET_RESULTS(state, payload: any) {
            state.title = payload.title;
            state.intro = payload.intro;
            state.results = payload.html;
            state.pages = payload.pages;
            state.page = parseInt(payload.page, 10) || 1;
            state.count = payload.count;
            // state.geojson = payload.geojson;
        },

        // SET_GEOJSON(state, payload: any) {
        //     state.geojson = payload;
        // },

        // ADD_GEOJSON_FEATURES(state, feature: GeoJsonFeature) {
        //     state.geojson.push(feature);
        // },

        SET_MAP_CENTER(state, payload) {
            state.mapCenter = payload;
        },

        SET_MAP_ZOOM(state, payload) {
            state.mapZoom = payload;
        },

        ADD_ALERT(state, payload: string) {
            state.alerts.push(payload);
        },

        DISABLE(state, payload: string) {
            if (!state.disabled.includes(payload)) {
                state.disabled.push(payload);
            }
        },

        ENABLE(state, payload: string) {
            const i = state.disabled.indexOf(payload);
            if (i > -1) {
                state.disabled.splice(i, 1);
            }
        },
    },

    actions: {

        toggleFilter({ commit, state, getters }, filter) {

            if (state.appDisabled) {
                return;
            }

            if (getters.moreOpened && state.focus !== 'more') {
                commit('SET_FOCUS', 'more');
            } else if (state.focus === filter) {
                commit('SET_FOCUS', null);
            } else {
                commit('SET_FOCUS', filter);
            }
        },

        dismissFilters({commit, getters}, payload) {
            commit('SET_FOCUS', null);
        },

        setFiltersEnabled({commit, getters, state}, payload: any) {

            const { filterNames, enabled } = payload;
            const MUTATION = enabled ? 'ENABLE' : 'DISABLE';
            for (const name of filterNames) {
                const filter = getters.filterByName(name);
                // console.log('setFiltersEnabled:', name, filter);
                switch (filter.type) {
                    case 'minmax':
                    case 'minmax-manual':
                        commit('APPLY_FILTER_MIN', {filter: filter.name, min: null});
                        commit('APPLY_FILTER_MAX', {filter: filter.name, max: null});
                        commit(MUTATION, filter.name);
                        break;
                    case 'multi':
                        commit('APPLY_FILTER_MULTI', {filter: filter.name, value: []});
                        commit(MUTATION, filter.name);
                        break;
                }
            }
        },

        applySort({ commit, dispatch }, option: string) {
            commit('APPLY_SORT', option);
            commit('SET_PAGE', 1);
            commit('SET_FOCUS', '');
            dispatch('fetch');
        },

        applyFilter({ commit, dispatch }, payload) {
            commit('APPLY_FILTER', payload);
            dispatch('fetch');
            dispatch('toggleFilter', payload.filter);
        },

        applyBuilding({ commit, dispatch, getters, state }, payload) {

            commit('APPLY_FILTER', payload);
            // console.log('Setting Building...', payload);

            const filter = getters.filterByName(payload.filter);

            // check if applying this filter should disable other filters
            if (filter.disables) {
                const enabled = payload.option == null;
                dispatch('setFiltersEnabled', {filterNames: filter.disables, enabled});
            }
            dispatch('fetch');
            dispatch('toggleFilter', payload.filter);

        },

        applyFilterMulti({ commit, dispatch }, payload) {
            commit('APPLY_FILTER_MULTI', payload);
            dispatch('fetch');
        },

        applyFilterMin({ commit, dispatch, state, getters }, payload) {
            commit('APPLY_FILTER_MIN', payload);
            dispatch('fetch');
        },

        applyFilterMax({ commit, dispatch, state, getters }, payload) {
            commit('APPLY_FILTER_MAX', payload);
            dispatch('fetch');
        },

        applyFilterFrom({ commit, dispatch, state, getters }, payload) {
            commit('APPLY_FILTER_FROM', payload);
            dispatch('toggleFilter', payload.filter);
            dispatch('fetch');
        },

        applyFilterTo({ commit, dispatch, state, getters }, payload) {
            commit('APPLY_FILTER_TO', payload);
            dispatch('toggleFilter', payload.filter);
            dispatch('fetch');
        },

        changePage({commit, dispatch, state}, payload) {
            commit('SET_PAGE', payload);
            dispatch('fetch', true);
        },

        setAlert({commit, state, getters}) {
            commit('SET_LOADING', true);
            commit('ADD_ALERT', getters.currentHash);

            const config = {
                headers: { 'X-CSRFToken': Cookies.get('csrftoken') },
            };

            axios.post(state.endpoints.listings, getters.selectedQuery, config)
            .then((response: { data: any }) => {
                const countOutput = response.data.count ? '(' + response.data.count + ')' : '';
                for (const el of document.getElementsByClassName('alerts-count')) {
                    el.innerHTML = countOutput;
                }
            })
            .catch((e: any) => {
                console.log('Error while setting alert...');
                console.log(e);
                // this.errors.push(e);
            })
            .finally(() => {
                commit('SET_LOADING', false);
                // dispatch hashChangeEvent for external scripts to listen for
                if (typeof window.hashChangeEvent !== "undefined") {
                    window.dispatchEvent(window.hashChangeEvent);
                }
            });
        },

        setMapCenter({ commit, dispatch, getters }, payload: any) {
            commit('SET_MAP_CENTER', [payload.center.lng, payload.center.lat]);
            if (payload.fetch) {
                // fetch new cards
                dispatch('fetch');
            } else {
                dispatch('pushHistory')
            }
        },

        setMapZoom({ commit, dispatch, getters }, payload) {
            commit('SET_MAP_ZOOM', payload);
            dispatch('pushHistory');
        },

        pushHistory({ commit, dispatch, getters }, routeName) {

            const currentRoute = router.currentRoute.value;

            if (!routeName) {
                routeName = currentRoute.name
            }

            routeName = (routeName as string).replace('wv', 'nv');

            switch (currentRoute.name) {
                // on any results list page or map page
                case 'listings-mls-nv-results':
                case 'sales-mls-nv-results':
                case 'listings-mls-nv-map':
                case 'sales-mls-nv-map':
                    router.replace({name: currentRoute.name, params: {shortRegion: getters.shortRegion}, query: getters.serverData });
                    break;
                default:
                    router.push({name: routeName, params: {shortRegion: getters.shortRegion}, query: getters.serverData });
            }
        },

        fetch({ commit, dispatch, state, getters }, stay=false) {

            console.log('Grabbing listings from server...');

            const config = {
                params: {...getters.serverData, page: state.page },
                paramsSerializer(params: any) {
                    return qs.stringify(params, {arrayFormat: 'repeat'});
                },
            };

            commit('SET_LOADING', true);
            axios.get(state.endpoints.listings, config).then((response: { data: any }) => {

                commit('SET_RESULTS', response.data);

                if (!stay) {
                    dispatch('pushHistory', response.data.route);
                }
            })
            .catch((e: any) => {
                console.log('Error while fecthing listings...');
                console.log(e);
            })
            .finally(() => {
                commit('SET_LOADING', false);
                // dispatch hashChangeEvent for external scripts to listen for
                if (typeof window.hashChangeEvent !== "undefined") {
                    window.dispatchEvent(window.hashChangeEvent);
                }
            });
        },

    },

});

